const FALLBACK_COLOR = '#12314a';

const normalizeHex = (value, fallback = FALLBACK_COLOR) => {
  const color = String(value || '').trim();
  const shortMatch = color.match(/^#([0-9a-f]{3})$/i);
  if (shortMatch) {
    return `#${shortMatch[1].split('').map((character) => character.repeat(2)).join('')}`.toLowerCase();
  }

  return /^#[0-9a-f]{6}$/i.test(color) ? color.toLowerCase() : fallback;
};

const toRgb = (color) => {
  const normalized = normalizeHex(color);
  return {
    r: parseInt(normalized.slice(1, 3), 16),
    g: parseInt(normalized.slice(3, 5), 16),
    b: parseInt(normalized.slice(5, 7), 16),
  };
};

const toHex = ({ r, g, b }) => {
  const channel = (value) => Math.round(value).toString(16).padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
};

const mix = (color, target, amount) => {
  const sourceRgb = toRgb(color);
  const targetRgb = toRgb(target);
  const weight = Math.max(0, Math.min(1, amount));

  return toHex({
    r: sourceRgb.r + (targetRgb.r - sourceRgb.r) * weight,
    g: sourceRgb.g + (targetRgb.g - sourceRgb.g) * weight,
    b: sourceRgb.b + (targetRgb.b - sourceRgb.b) * weight,
  });
};

const luminance = (color) => {
  const { r, g, b } = toRgb(color);
  const channels = [r, g, b].map((channel) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
};

const contrast = (first, second) => {
  const firstLuminance = luminance(first);
  const secondLuminance = luminance(second);
  return (Math.max(firstLuminance, secondLuminance) + 0.05)
    / (Math.min(firstLuminance, secondLuminance) + 0.05);
};

const ensureChartContrast = (color, background, target) => {
  if (contrast(color, background) >= 3) return color;

  for (let amount = 0.12; amount <= 0.72; amount += 0.12) {
    const candidate = mix(color, target, amount);
    if (contrast(candidate, background) >= 3) return candidate;
  }

  return target;
};

export const createStatusReportPalette = (tenantColor, colorMode = 'light') => {
  const accent = normalizeHex(tenantColor);
  const isDark = colorMode === 'dark';
  const surface = isDark ? '#111827' : '#ffffff';
  const contrastTarget = isDark ? '#ffffff' : '#111827';
  const onAccent = contrast(accent, '#ffffff') >= contrast(accent, '#111827')
    ? '#ffffff'
    : '#111827';
  const accentReadable = ensureChartContrast(accent, surface, contrastTarget);
  const chartCurrent = isDark && contrast(accent, surface) < 1.35
    ? accentReadable
    : accent;

  return {
    accent,
    accentReadable,
    onAccent,
    onAccentMuted: mix(onAccent, accent, 0.24),
    accentSoft: mix(accent, surface, isDark ? 0.84 : 0.9),
    accentBorder: mix(accent, surface, isDark ? 0.58 : 0.68),
    chartCurrent,
    chartUpcoming: mix(chartCurrent, '#ffffff', 0.16),
    chartPrior: mix(chartCurrent, '#ffffff', isDark ? 0.46 : 0.52),
    chartBorder: accentReadable,
  };
};
